import { useCallback, useState, cloneElement } from 'react';
import { Dropdown } from 'antd';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import UserIcon from 'assets/images/user.svg?react';
import AntdArrow from 'assets/images/ant-arrow.svg?react';
import { removeFromStorage } from 'helpers/storage';
import { ROUTES } from 'const';
import {
  NAVIGATION_LABELS,
  MY_INFORMATION_PAGE_TRANSLATIONS,
} from 'const/translations';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  WAIBI_ACCESS_TOKEN,
  WAIBI_REFRESH_TOKEN,
} from 'shared/config';
import {
  CART_PACKAGES_LOCAL_STORAGE_KEY,
  INITIAL_DATASET_LOCAL_STORAGE_KEY,
  USER_SAVED_PACKAGES_LOCAL_STORAGE_KEY,
} from 'const/cartPage';
import { redirect } from 'helpers/common';
import { logout } from 'modules/loggedUserInfo/services';
import { ADDITIONAL_USERS_LOCAL_STORAGE_KEYS } from 'const/additionalUsers';
import Styles from './LogoutDropdown.module.css';

const { DATASET_KEY, DELETED_USERS_KEY } = ADDITIONAL_USERS_LOCAL_STORAGE_KEYS;

const propTypes = {
  userInfoFullName: PropTypes.string,
  isCurrentCompanyActivated: PropTypes.bool,
  isMainUser: PropTypes.bool,
};

export const LogoutDropdown = ({
  userInfoFullName,
  isCurrentCompanyActivated,
  isMainUser,
}) => {
  const [arrowRotateDeg, setArrowRotateDeg] = useState(0);

  const arrowRotateStyle = {
    transform: `rotate(${arrowRotateDeg}deg)`,
  };

  const onVisibleChange = useCallback(
    (visible) => {
      if (visible) {
        setArrowRotateDeg(180);
        return;
      }
      setArrowRotateDeg(0);
    },
    [setArrowRotateDeg]
  );

  const onMenuItemClick = useCallback(() => {
    setArrowRotateDeg(0);
  }, [setArrowRotateDeg]);

  const onLogout = () => {
    logout().then(({ data }) => {
      if (data?.url) {
        redirect(data.url);

        removeFromStorage({
          key: [
            WAIBI_ACCESS_TOKEN,
            WAIBI_REFRESH_TOKEN,
            INITIAL_DATASET_LOCAL_STORAGE_KEY,
            USER_SAVED_PACKAGES_LOCAL_STORAGE_KEY,
            CART_PACKAGES_LOCAL_STORAGE_KEY,
            DATASET_KEY,
            DELETED_USERS_KEY,
          ],
        });
        removeFromStorage({
          key: [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY],
          storage: sessionStorage,
        });
      }
    });
  };

  const menuItems = [
    {
      key: ROUTES.CONTACTS,
      label: (
        <Link to={ROUTES.CONTACTS} onClick={onMenuItemClick}>
          {NAVIGATION_LABELS.CONTACTS}
        </Link>
      ),
    },
    {
      key: ROUTES.MY_INFORMATION,
      label: (
        <Link to={ROUTES.MY_INFORMATION} onClick={onMenuItemClick}>
          {MY_INFORMATION_PAGE_TRANSLATIONS.TITLE}
        </Link>
      ),
    },
    ...(isCurrentCompanyActivated && isMainUser
      ? [
          {
            key: ROUTES.ADDITIONAL_USERS,
            label: (
              <Link to={ROUTES.ADDITIONAL_USERS} onClick={onMenuItemClick}>
                {NAVIGATION_LABELS.ADDITIONAL_USERS}
              </Link>
            ),
          },
        ]
      : []),
    {
      key: 'logout',
      label: (
        <Link to={ROUTES.LOGIN} onClick={onLogout}>
          {NAVIGATION_LABELS.DISCONNECT}
        </Link>
      ),
    },
  ];

  return (
    <Dropdown
      menu={{
        items: menuItems,
        className: Styles.dropDownMenu,
        onClick: onMenuItemClick,
      }}
      arrow
      trigger={['click']}
      popupRender={(menu) => (
        <div className={Styles.dropDownWindow}>{cloneElement(menu)}</div>
      )}
      placement="bottomRight"
      onOpenChange={onVisibleChange}
    >
      <div
        className={clsx(Styles.dropDownTrigger, {
          [Styles.transparentTrigger]: arrowRotateDeg === 180,
        })}
      >
        <UserIcon className={Styles.userIcon} />
        <span>{userInfoFullName}</span>
        <i
          aria-label="icon: down"
          style={arrowRotateStyle}
          className="ant-select-arrow-icon"
        >
          <AntdArrow />
        </i>
      </div>
    </Dropdown>
  );
};

LogoutDropdown.propTypes = propTypes;
